import { Button } from "@fieldwork/ui/components/button";
import { cn } from "@fieldwork/ui/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { NotConnected, useServer } from "@/components/server-hooks";
import { Markdown } from "@/components/markdown";
import { Chip, errorMessage } from "@/components/ui-bits";
import { act } from "@/lib/act";

export const Route = createFileRoute("/_auth/servers/$id/chat")({
  component: ChatTab,
});

type Ev =
  | { seq: number; kind: "user" | "assistant" | "error"; text: string }
  | { seq: number; kind: "tool"; id: string; name: string; input: Record<string, string> }
  | { seq: number; kind: "tool_result"; id: string; error: boolean; text: string }
  | { seq: number; kind: "permission"; id: string; tool: string; title: string; input: Record<string, string> }
  | { seq: number; kind: "decision"; id: string; allow: boolean }
  | { seq: number; kind: "denied"; tool: string; reason: string }
  | { seq: number; kind: "done"; cost: number | null; error: boolean; reason: string | null };
type Poll = { events: Ev[]; busy: boolean; waiting: string[] };

const EXAMPLES = [
  "I started advising Acme on their API launch. Sarah Lee is my contact there; their Slack channel is #acme-launch.",
  "Change the partner reply time to 4 hours.",
  "Anything from my accountant about invoices should be high priority.",
  "Which projects do you know about, and what's mapped to each?",
];

function ChatTab() {
  const { id } = Route.useParams();
  const server = useServer(id).data;
  const connected = !!server?.hub.connected;
  const [events, setEvents] = useState<Ev[]>([]);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState<string[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const last = useRef(0);
  const bottom = useRef<HTMLDivElement>(null);
  const pollNow = useRef<() => void>(() => {});

  // Poll for new events: every second while Claude works, every 15 s when idle, and not at all while the tab
  // is hidden (each poll is a request through the panel and its database; a forgotten tab used to poll all
  // night). Coming back to the tab polls at once.
  useEffect(() => {
    if (!connected) return;
    let stop = false;
    let inFlight = false;
    let again = false; // an action happened while a poll was running: poll once more right after it
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = (ms: number) => {
      clearTimeout(timer);
      if (!stop && document.visibilityState === "visible") timer = setTimeout(tick, ms);
    };
    async function tick() {
      if (stop || document.visibilityState !== "visible") return;
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      try {
        const r = await act<Poll>(id, "chat_poll", { after: last.current });
        if (stop) return;
        if (r.events.length) {
          last.current = r.events[r.events.length - 1]!.seq;
          setEvents((e) => [...e, ...r.events]);
        }
        setBusy(r.busy);
        setWaiting(r.waiting);
        schedule(r.busy ? 1000 : 15_000);
      } catch {
        schedule(15_000);
      } finally {
        inFlight = false;
        if (again && !stop) {
          again = false;
          schedule(0);
        }
      }
    }
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
      else clearTimeout(timer);
    };
    document.addEventListener("visibilitychange", onVisible);
    pollNow.current = () => {
      clearTimeout(timer);
      tick();
    };
    tick();
    return () => {
      stop = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [id, connected]);

  // Braces matter: newer Chrome returns a Promise from scrollIntoView, and an effect must return
  // nothing or a cleanup function (React calls whatever it returns: "l is not a function").
  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [events.length]);

  const call = async (action: Parameters<typeof act>[1], params: Record<string, unknown> = {}) => {
    try {
      const r = await act(id, action, params);
      pollNow.current(); // a message, approval or stop changes what's on screen: fetch it now, not in 15 s
      return r;
    } catch (e) {
      toast.error(errorMessage(e));
      return null;
    }
  };
  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    const r = await call("chat_send", { text });
    setSending(false);
    if (r) {
      setText("");
      setBusy(true);
    }
  };
  const results = new Map(events.filter((e) => e.kind === "tool_result").map((e) => [(e as { id: string }).id, e]));
  const decisions = new Map(events.filter((e) => e.kind === "decision").map((e) => [(e as { id: string }).id, (e as { allow: boolean }).allow]));

  const lastCost = [...events].reverse().find((e) => e.kind === "done" && e.cost != null) as { cost: number } | undefined;
  const newConversation = async () => {
    if (!window.confirm("Start a new conversation? The current one is archived on your server.")) return;
    if (await call("chat_reset")) {
      setEvents([]);
      last.current = 0;
    }
  };
  const waitingForMe = waiting.some((w) => !decisions.has(w));

  // Not ServerPage: the conversation is a centred 860px column that fills the height, with the composer at the bottom.
  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-[#262523] px-5 py-5 sm:px-10">
        <div className="flex flex-col gap-1">
          <h1 className="m-0 font-mono text-[26px] font-bold tracking-[-0.02em]">Assistant</h1>
          <p className="m-0 max-w-[74ch] text-[13px] text-[#A8A59D]">
            Ask Claude to change how Fieldwork works for you. It runs on your server with your own Claude account (subscription
            or API key, set in AI &amp; cost); you approve every change, and each one is saved as a git commit you can undo.
          </p>
        </div>
        {connected && !busy && events.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={newConversation}>
              New conversation
            </Button>
          </div>
        )}
      </header>
      <div className="mx-auto box-border flex w-full max-w-[860px] flex-1 flex-col gap-[18px] px-5 pt-7 pb-6 sm:px-10">
        {!connected ? (
          <NotConnected />
        ) : (
          <>
            {events.map((e) => {
              switch (e.kind) {
                case "user":
                  return (
                    <div key={e.seq} className="max-w-[80%] self-end rounded-[10px_10px_2px_10px] bg-[#2A2927] px-4 py-3 whitespace-pre-wrap">
                      {e.text}
                    </div>
                  );
                case "assistant":
                  return (
                    <div key={e.seq} className="max-w-[72ch]">
                      <Markdown text={e.text} />
                    </div>
                  );
                case "tool": {
                  const r = results.get(e.id) as { text: string; error: boolean } | undefined;
                  return (
                    <details key={e.seq} className="rounded-[4px] bg-[#161615] px-3 py-2">
                      <summary className="cursor-pointer font-mono text-xs text-[#A8A59D]">
                        {e.name} · {e.input.file_path ?? e.input.command ?? e.input.pattern ?? ""}
                        {r?.error ? <span className="text-[#F48A80]"> · failed</span> : ""}
                      </summary>
                      <pre className="mt-2 mb-0 max-h-60 overflow-auto rounded-[4px] bg-[#0E0E0D] p-3 font-mono text-[11px] whitespace-pre-wrap">
                        {JSON.stringify(e.input, null, 1)}
                      </pre>
                      {r && (
                        <pre className="mt-2 mb-0 max-h-60 overflow-auto rounded-[4px] bg-[#0E0E0D] p-3 font-mono text-[11px] whitespace-pre-wrap">{r.text}</pre>
                      )}
                    </details>
                  );
                }
                case "permission": {
                  const open = waiting.includes(e.id) && !decisions.has(e.id);
                  const decided = decisions.get(e.id);
                  return (
                    <section
                      key={e.seq}
                      aria-label="Approval"
                      className={cn(
                        "flex flex-col gap-3 rounded-md bg-[#1A1A19] px-[18px] py-4",
                        open ? "shadow-[inset_0_0_0_1px_rgba(232,163,61,0.55)]" : "shadow-[inset_0_0_0_1px_#2A2927]",
                      )}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2.5">
                        <span className="font-mono text-[13px] font-bold">{e.title}</span>
                        {open ? (
                          <Chip value="warn" label="waiting for you" />
                        ) : (
                          <Chip value={decided ? "ok" : "neutral"} label={decided === undefined ? "expired" : decided ? "approved" : "denied"} />
                        )}
                      </div>
                      {Object.entries(e.input).map(([k, v]) => (
                        <div key={k} className="flex flex-col gap-1">
                          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">{k}</span>
                          <pre className="m-0 max-h-60 overflow-auto rounded-[4px] bg-[#0E0E0D] px-3.5 py-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-[#CFCBC1]">
                            {v}
                          </pre>
                        </div>
                      ))}
                      {open && (
                        <div className="flex gap-2">
                          <Button onClick={() => call("chat_permission", { id: e.id, allow: true })}>Approve</Button>
                          <Button variant="outline" onClick={() => call("chat_permission", { id: e.id, allow: false })}>
                            Deny
                          </Button>
                        </div>
                      )}
                    </section>
                  );
                }
                case "denied":
                  return (
                    <p key={e.seq} className="m-0 text-[13px] text-[#F0B65A]">
                      Blocked {e.tool}: {e.reason}
                    </p>
                  );
                case "error":
                  return (
                    <p key={e.seq} className="m-0 text-[13px] text-[#F48A80]">
                      {e.text}
                    </p>
                  );
                case "done":
                  return e.error ? (
                    <p key={e.seq} className="m-0 font-mono text-xs text-[#A8A59D]">
                      stopped ({e.reason ?? "error"})
                    </p>
                  ) : null;
                default:
                  return null;
              }
            })}
            {busy && (
              <div className="flex items-center gap-2.5 text-[13px] text-[#A8A59D]">
                <span aria-hidden="true" className={cn("size-2 rounded-full", waitingForMe ? "bg-[#F0B65A]" : "animate-pulse bg-[#7DA7F0]")} />
                {waitingForMe ? "Claude is waiting for your approval…" : "Claude is working…"}
              </div>
            )}
            <div ref={bottom} />
            <div className="grow" />

            <form
              className="flex flex-col gap-2.5 rounded-lg bg-[#1A1A19] p-3.5 shadow-[inset_0_0_0_1px_#3A3935]"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <label className="flex flex-col">
                <span className="sr-only">Message</span>
                <textarea
                  rows={3}
                  className="resize-none border-0 bg-transparent px-1 py-1.5 text-[15px] leading-normal text-[#ECEAE4] outline-none placeholder:text-[#8C8981]"
                  value={text}
                  placeholder="Tell Claude what to change…"
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
                  }}
                  disabled={busy}
                />
              </label>
              <div className="flex flex-wrap items-center justify-between gap-2.5">
                <span className="text-xs text-[#A8A59D]">
                  ⌘ Enter to send{lastCost ? ` · session so far ≈ $${lastCost.cost.toFixed(2)}` : ""}
                </span>
                <div className="flex gap-2">
                  {busy && (
                    <Button type="button" variant="outline" onClick={() => call("chat_interrupt")}>
                      Stop
                    </Button>
                  )}
                  <Button
                    type="submit"
                    className="disabled:bg-[#3A3935] disabled:text-[#A8A59D] disabled:opacity-100"
                    disabled={busy || sending || !text.trim()}
                  >
                    Send
                  </Button>
                </div>
              </div>
            </form>
            {events.length === 0 && (
              <div className="flex flex-wrap gap-2">
                <span className="self-center text-xs text-[#A8A59D]">Try:</span>
                {EXAMPLES.map((x) => (
                  <button
                    key={x}
                    type="button"
                    className="min-h-8 rounded-2xl bg-[#222220] px-3 py-1 text-left text-[13px] text-[#CFCBC1] hover:text-[#ECEAE4]"
                    onClick={() => setText(x)}
                  >
                    {x}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
